import React from 'react';
import { CheckCircle, Users, Shield, Leaf, Award, MapPin } from 'lucide-react';

const AboutUs = () => {
  const services = [
    {
      title: 'Daily Rentals',
      description: 'Perfect for short trips and errands with flexible daily rates.',
      icon: '📅'
    },
    {
      title: 'Weekly Rentals',
      description: 'Extended stay options offering flexibility and affordability.',
      icon: '📆'
    },
    {
      title: 'Monthly Rentals',
      description: 'Long-term solutions with unbeatable rates for extended use.',
      icon: '🗓️'
    }
  ];

  const features = [
    {
      title: 'Wide Selection',
      description: 'Diverse fleet of well-maintained scooters and motorbikes',
      icon: <Award className="w-6 h-6" />
    },
    {
      title: 'Quality Assurance',
      description: 'Regular maintenance ensuring smooth and secure rides',
      icon: <Shield className="w-6 h-6" />
    },
    {
      title: 'Convenience',
      description: 'Easy pick-up and drop-off at our Pune head office',
      icon: <MapPin className="w-6 h-6" />
    },
    {
      title: 'Affordability',
      description: 'Competitive rates with best value for your money',
      icon: <CheckCircle className="w-6 h-6" />
    },
    {
      title: 'Customer-Centric',
      description: 'Dedicated team providing exceptional service',
      icon: <Users className="w-6 h-6" />
    },
    {
      title: 'Eco-Friendly',
      description: 'Fuel-efficient and environmentally conscious options',
      icon: <Leaf className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">
            About Vegatas Bike Rental Service
          </h1>
          <p className="text-xl text-center text-blue-100 max-w-3xl mx-auto">
            Your go-to destination for hassle-free two-wheeler rentals in Pune and beyond
          </p>
        </div>
      </div>

      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Welcome to Vegatas
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We take pride in providing a convenient and affordable solution for all your scooter and motorbike rental needs. Whether you're a local resident looking for a convenient mode of transportation or a tourist exploring the vibrant city of Pune, we'VEGATASt you covered.
            </p>
            <div className="flex items-start space-x-3 mt-6">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <p className="text-gray-600">
                Serving locals and tourists with reliable transportation solutions
              </p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg">
            <div className="aspect-video bg-blue-200 rounded-lg flex items-center justify-center">
              <span className="text-6xl">🏍️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              At Vegatas Bike Rental Service, our mission is to offer a seamless and enjoyable riding experience while promoting sustainable and eco-friendly transportation options. We believe that every journey should be a memorable adventure, and we're here to make that happen for you.
            </p>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Our Services
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 border border-gray-100"
            >
              <div className="text-5xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-gradient-to-br from-blue-50 to-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Choose Vegatas?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-lg flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl p-12">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-blue-100">Happy Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-blue-100">Bikes Available</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-blue-100">Support</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-blue-100">Satisfaction</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Book your bike today and experience the freedom of riding with Vegatas
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg">
              Book Now
            </button>
            <button className="bg-white hover:bg-gray-50 text-blue-600 font-semibold px-8 py-4 rounded-lg border-2 border-blue-600 transition-colors duration-300">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
